import { css } from '@emotion/react';
import { SessionUser } from '@mythos/utils/Session';
import { useCallback, useState } from 'react';
import { CardCountTheme } from './CardCountsView';
import { ColorPalette } from './ColorPalette';
import { graphql } from './generated/gql';
import { executeGQL, useGraphQLQuery } from './GraphQLHelpers';
import {
  PlayerSummaryCountMode,
  PlayerSummaryFavorMode,
} from './PlayerSummaryView';
import QueryStateRenderer from './QueryStateRenderer';

export type UserPrefs = {
  disableAnimations?: boolean;
  muteSFX?: boolean;
  playerSummaryCountMode?: PlayerSummaryCountMode;
  playerSummaryFavorMode?: PlayerSummaryFavorMode;
  cardCountTheme?: CardCountTheme;
};

export function useUserPref<TKey extends keyof UserPrefs>(
  pref: TKey,
  defaultValue: NonNullable<UserPrefs[TKey]>,
): [
  NonNullable<UserPrefs[TKey]>,
  (value: NonNullable<UserPrefs[TKey]>) => void,
] {
  const [value, setValue] = useState<NonNullable<UserPrefs[TKey]>>(
    getUserPrefs()[pref] ?? defaultValue,
  );
  const wrappedSetValue = useCallback(
    (value: NonNullable<UserPrefs[TKey]>) => {
      setValue(value);
      setUserPrefs({ [pref]: value });
    },
    [pref],
  );
  return [value, wrappedSetValue];
}

export function getUserPrefs(): UserPrefs {
  const prefs = localStorage.getItem('userPrefs');
  if (prefs) {
    return JSON.parse(prefs);
  }
  return {};
}

export function setUserPrefs(prefs: Partial<UserPrefs>) {
  const existing = getUserPrefs();
  localStorage.setItem('userPrefs', JSON.stringify({ ...existing, ...prefs }));
}

const AvailableProfilePicturesQuery = graphql(`
  query AvailableProfilePictures {
    availableProfilePictures {
      key
      url
      description
    }
  }
`);

const ChangeUserNameMutation = graphql(`
  mutation ChangeUserName($id: ID!, $name: String!) {
    changeUserName(id: $id, name: $name) {
      id
    }
  }
`);
const ChangeProfilePictureMutation = graphql(`
  mutation ChangeProfilePicture($key: String!) {
    changeProfilePicture(key: $key) {
      id
    }
  }
`);

export function UserPreferenceMenu(props: { user: SessionUser }) {
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [newName, setNewName] = useState(props.user.name);
  const [disableAnimations, setDisableAnimations] = useUserPref(
    'disableAnimations',
    false,
  );
  const [muteSFX, setMuteSFX] = useUserPref('muteSFX', false);
  const [pfpDescriptionText, setPFPDescriptionText] = useState('');

  const onNameSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (requestInProgress) {
        return;
      }
      setRequestInProgress(true);
      executeGQL(ChangeUserNameMutation, {
        id: props.user.id,
        name: newName,
      }).then(() => {
        setRequestInProgress(false);
        location.reload();
      });
    },
    [newName, requestInProgress, setRequestInProgress],
  );

  // XXX: fix
  const canChangeProfilePicture = props.user.canChangeName;

  const onProfilePictureChange = useCallback(
    (key: string) => {
      setRequestInProgress(true);
      executeGQL(ChangeProfilePictureMutation, { key }).then(() => {
        setRequestInProgress(false);
        location.reload();
      });
    },
    [requestInProgress, setRequestInProgress],
  );

  const profilePicturesState = useGraphQLQuery(AvailableProfilePicturesQuery);

  return (
    <div css={UserPreferenceMenuStyles.container}>
      <h2 css={UserPreferenceMenuStyles.title}>User Settings</h2>
      {props.user.canChangeName && (
        <form onSubmit={onNameSubmit}>
          <label>
            New Name:
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </label>
          <input type="submit" value="Submit" />
        </form>
      )}
      {canChangeProfilePicture && (
        <div>
          <h3>Profile Picture</h3>
          <QueryStateRenderer state={profilePicturesState}>
            {(data) => (
              <>
                <div css={UserPreferenceMenuStyles.profilePicturesContainer}>
                  {data.availableProfilePictures.map((pfp) => (
                    <img
                      key={pfp.key}
                      src={pfp.url}
                      alt={pfp.key}
                      css={UserPreferenceMenuStyles.profilePicture}
                      onClick={() => onProfilePictureChange(pfp.key)}
                      onMouseEnter={() =>
                        setPFPDescriptionText(pfp.description || ' ')
                      }
                      onMouseLeave={() => setPFPDescriptionText(' ')}
                    />
                  ))}
                </div>
                <div
                  id="profile-picture-tooltip"
                  css={UserPreferenceMenuStyles.profilePictureDescriptionArea}
                >
                  {pfpDescriptionText}
                </div>
              </>
            )}
          </QueryStateRenderer>
        </div>
      )}
      <label>
        <input
          type="checkbox"
          checked={disableAnimations}
          onChange={(e) => {
            setDisableAnimations(e.target.checked);
          }}
        />
        Disable Animations
      </label>
      <br />
      <label>
        <input
          type="checkbox"
          checked={muteSFX}
          onChange={(e) => {
            setMuteSFX(e.target.checked);
          }}
        />
        Mute Sound Effects
      </label>
    </div>
  );
}
const UserPreferenceMenuStyles = {
  container: css({
    backgroundColor: ColorPalette.lightBG,
  }),
  title: css({
    fontSize: '1.5em',
    fontWeight: 'bold',
    marginBottom: '1em',
  }),

  profilePicturesContainer: css({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    maxWidth: '500px',
  }),

  profilePicture: css({
    width: '50px',
    height: '50px',
    objectFit: 'cover',
    margin: '0.25em',
  }),

  profilePictureDescriptionArea: css({
    fontSize: '0.8em',
    minHeight: '1.5em',
  }),
};
